import type { CSSProperties } from 'react'
import { useMemo, useRef } from 'react'
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { getFloatingPositionStyle } from 'LuxKit/utils/floatingPositionStyle'
import type { TextDirection } from 'lib/dom/logicalScroll'

// Mic sits ~14px inside the composer; design gap below it is ~10px.
const PROMPT_OFFSET = 24
const VIEWPORT_PADDING = 8

function getArrowStyle(x: number | undefined, textDirection: TextDirection): CSSProperties {
  if (x === undefined) return {}
  // floating-ui measures from the physical left edge.
  return textDirection === 'rtl' ? { insetInlineEnd: x } : { insetInlineStart: x }
}

/**
 * Pins a voice prompt to the microphone with its tail on the mic's centre. Fixed positioning
 * keeps it clear of the landing page's clipped containers while staying in the composer's DOM order.
 */
export function useVoicePromptPosition(anchorElement: HTMLElement | null, textDirection: TextDirection) {
  const arrowRef = useRef<HTMLSpanElement>(null)
  const middleware = useMemo(() => [
    offset(PROMPT_OFFSET),
    flip({ padding: VIEWPORT_PADDING }),
    shift({ padding: VIEWPORT_PADDING }),
    arrow({ element: arrowRef }),
  ], [])

  const { x, y, strategy, placement, refs, middlewareData } = useFloating<HTMLElement>({
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    elements: { reference: anchorElement },
    middleware,
  })

  const floatingStyle = useMemo(
    () => getFloatingPositionStyle(strategy, x, y, textDirection),
    [strategy, textDirection, x, y],
  )
  const arrowX = middlewareData.arrow?.x
  const arrowStyle = useMemo(() => getArrowStyle(arrowX, textDirection), [arrowX, textDirection])

  return {
    setFloating: refs.setFloating,
    floatingStyle,
    arrowRef,
    arrowStyle,
    isAbove: placement.startsWith('top'),
  }
}
